/**
 * One vocabulary for easement and right-of-way corridors, shared by the live
 * report pipeline, the sample library and every PDF/exhibit renderer.
 *
 * Before this existed, each corridor was drawn in the same amber dashed line
 * with a single catch-all legend row, so a reader could not tell a public road
 * right-of-way from a utility easement crossing the back of the lot. Both the
 * live reports and the samples now describe corridors by kind and every
 * renderer styles and labels them the same way.
 */
import {
  PRIVATE_SYMBOLS,
  PUBLIC_SYMBOLS,
  type LineSymbol,
  type SymbolFamily,
} from "./map-symbology";

function pick(sym: LineSymbol) {
  return {
    color: sym.rgb,
    dash: sym.dash,
    legend: sym.legend,
    hex: sym.hex,
    family: sym.family,
  };
}

export type CorridorKind = "row" | "utility" | "access" | "drainage" | "rail" | "other";

const KINDS: CorridorKind[] = ["row", "utility", "access", "drainage", "rail", "other"];

/**
 * Line color, dash pattern and plain-English legend wording per kind, taken
 * from the one symbology table so screen, PDF and exhibit renderers agree.
 */
export const CORRIDOR_STYLE: Record<
  CorridorKind,
  { color: [number, number, number]; dash: number[]; legend: string; hex: string; family: SymbolFamily }
> = {
  row: pick(PUBLIC_SYMBOLS.row),
  utility: pick(PRIVATE_SYMBOLS.utility),
  access: pick(PRIVATE_SYMBOLS.access),
  drainage: pick(PRIVATE_SYMBOLS.drainage),
  rail: pick(PUBLIC_SYMBOLS.rail),
  other: pick(PRIVATE_SYMBOLS.other),
};


/** Draw order: wide public corridors first, private strips on top of them. */
export const CORRIDOR_DRAW_ORDER: CorridorKind[] = ["rail", "row", "drainage", "utility", "access", "other"];

/**
 * Normalizes whatever a data source calls a corridor into one of our kinds.
 * Accepts our own kinds, the enhanced-parcel kinds (`roadway`), and free-text
 * county attributes such as "PUBLIC UTILITY ESMT" or "DRAINAGE & UTILITY".
 */
export function normalizeCorridorKind(value: string | null | undefined): CorridorKind {
  const raw = String(value ?? "").trim().toLowerCase();
  if (!raw) return "other";
  if ((KINDS as string[]).includes(raw)) return raw as CorridorKind;
  if (/road|street|roadway|r\/?o\/?w|right[\s-]?of[\s-]?way|highway/.test(raw)) return "row";
  if (/rail|railroad|railway/.test(raw)) return "rail";
  if (/drain|storm|swale|detention/.test(raw)) return "drainage";
  if (/access|ingress|egress|driveway/.test(raw)) return "access";
  if (/util|electric|power|sewer|water|gas|pipeline|pue|telecom/.test(raw)) return "utility";
  return "other";
}

/** Kind inferred from a corridor label when the source carries no kind field. */
export function corridorKindFromLabel(label: string | null | undefined): CorridorKind {
  return normalizeCorridorKind(label);
}
